import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  ArrowLeft,
  Users,
  Ticket,
  Trophy,
  Send,
  TrendingUp,
  Clock,
  PieChart as PieIcon,
} from "lucide-react";

import BoostCampaignDialog from "@/components/vendor/BoostCampaignDialog";
import { toast } from "sonner";

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString("en-IN") : n;
const pct = (n) => `${(n || 0).toFixed(1)}%`;

const KPI = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="p-5 shadow-[var(--shadow-card)]" data-testid={testid || "analytics-kpi"}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-heading text-2xl md:text-3xl font-semibold mt-1 tabular-nums">
          {value}
        </p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className="h-10 w-10 rounded-xl bg-accent text-accent-foreground grid place-items-center">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

const FunnelBar = ({ label, value, max, color, hint }) => {
  const width = max > 0 ? Math.max(4, (value / max) * 100) : 4;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {fmt(value)} {hint ? <span className="ml-1 text-xs">({hint})</span> : null}
        </span>
      </div>
      <div className="mt-1.5 h-3 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${width}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const HourHeatmap = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.spins));
  return (
    <div
      className="grid grid-cols-12 sm:grid-cols-24 gap-1"
      style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}
      data-testid="analytics-hour-heatmap"
    >
      {data.map((d) => {
        const intensity = d.spins / max;
        return (
          <div
            key={d.hour}
            className="aspect-square rounded-sm flex items-end justify-center text-[8px] text-white/80"
            style={{
              backgroundColor:
                d.spins === 0
                  ? "hsl(var(--muted))"
                  : `hsl(var(--primary) / ${0.18 + intensity * 0.8})`,
            }}
            title={`${d.hour}:00 - ${d.spins} spin(s)`}
          >
            {d.hour}
          </div>
        );
      })}
    </div>
  );
};

export default function CampaignAnalyticsPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [boostOpen, setBoostOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .campaignStats(id)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  const distribution = data?.distribution || [];
  const series = data?.series || [];
  const hourSeries = data?.hour_series || [];
  const dowSeries = data?.dow_series || [];
  const funnel = data?.funnel || { leads: 0, coupons: 0, redeemed: 0 };

  const pieData = useMemo(
    () => distribution.map((d) => ({ name: d.label, value: d.count, color: d.color })),
    [distribution]
  );
  const topSlices = useMemo(() => distribution.slice(0, 8), [distribution]);

  if (loading)
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );

  if (err || !data)
    return (
      <Card className="p-10 text-center" data-testid="analytics-error">
        <p className="text-destructive">{err || "Analytics unavailable"}</p>
        <Link to="/vendor/campaigns" className="text-sm text-primary mt-3 inline-block">
          ← Back to campaigns
        </Link>
      </Card>
    );

  const primary = data.campaign?.primary_color || "hsl(var(--primary))";

  return (
    <div className="space-y-6" data-testid="campaign-analytics-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link
            to="/vendor/campaigns"
            className="inline-flex items-center text-sm text-muted-foreground hover:underline"
            data-testid="analytics-back-link"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to campaigns
          </Link>
          <h1
            className="font-heading text-3xl font-semibold mt-1"
            data-testid="analytics-campaign-name"
          >
            {data.campaign.name}
          </h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
            <Badge variant="outline" className="capitalize">{data.campaign.theme}</Badge>
            <Badge variant="outline" className="capitalize">{data.campaign.status}</Badge>
          </div>
        </div>
        <Link to={`/vendor/campaigns/${id}`}>
          <Button variant="outline" data-testid="analytics-edit-campaign">Edit campaign</Button>
        </Link>
        <Button
          onClick={() => setBoostOpen(true)}
          className="bg-[#F5A800] text-[#0A0A0A] hover:bg-[#F5A800]/90"
          data-testid="analytics-boost-cta"
        >
          <Trophy className="h-4 w-4 mr-2" /> Boost this campaign
        </Button>
      </div>

      <BoostCampaignDialog
        open={boostOpen}
        onOpenChange={setBoostOpen}
        campaign={data.campaign}
        onSuccess={() => toast.info("Featured slot activated — it may take a moment to appear on Explore.")}
      />

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          icon={Users}
          label={
            /* Per-campaign label follows the game type directly — no
               ambiguity since a campaign is exactly one game type. */
            data.campaign?.game_type === "scratch_card" ? "Total scratches" : "Total spins"
          }
          value={fmt(data.total_leads)}
          sub={`${fmt(data.leads_period)} in last 30 days`}
          testid="kpi-total-plays"
        />
        <KPI
          icon={Ticket}
          label="Coupons issued"
          value={fmt(data.total_coupons)}
          sub={pct(funnel.lead_to_coupon_pct) + " of leads"}
          testid="kpi-coupons-issued"
        />
        <KPI
          icon={Trophy}
          label="Redeemed"
          value={fmt(data.redeemed_coupons)}
          sub={pct(funnel.coupon_to_redeem_pct) + " redemption rate"}
          testid="kpi-redeemed"
        />
        <KPI
          icon={Clock}
          label="Avg redemption"
          value={
            data.avg_redemption_hours == null
              ? "—"
              : data.avg_redemption_hours < 24
                ? `${data.avg_redemption_hours}h`
                : `${(data.avg_redemption_hours / 24).toFixed(1)}d`
          }
          sub="Time from win to redeem"
          testid="kpi-avg-redemption"
        />
      </div>

      {/* Funnel + Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="p-5 lg:col-span-5 shadow-[var(--shadow-card)]" data-testid="analytics-funnel-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Conversion funnel</h2>
          </div>
          <div className="space-y-4">
            <FunnelBar
              label="Leads (plays)"
              value={funnel.leads}
              max={funnel.leads}
              color={primary}
            />
            <FunnelBar
              label="Coupons issued"
              value={funnel.coupons}
              max={funnel.leads || 1}
              color="hsl(var(--chart-2))"
              hint={pct(funnel.lead_to_coupon_pct)}
            />
            <FunnelBar
              label="Coupons redeemed"
              value={funnel.redeemed}
              max={funnel.leads || 1}
              color="hsl(var(--chart-4))"
              hint={pct(funnel.lead_to_redeem_pct) + " of leads"}
            />
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3 text-center text-sm">
            <div className="rounded-lg bg-muted p-3">
              <p className="text-xs text-muted-foreground">Lead → Coupon</p>
              <p className="font-heading text-lg font-semibold tabular-nums">
                {pct(funnel.lead_to_coupon_pct)}
              </p>
            </div>
            <div className="rounded-lg bg-muted p-3">
              <p className="text-xs text-muted-foreground">Coupon → Redeem</p>
              <p className="font-heading text-lg font-semibold tabular-nums">
                {pct(funnel.coupon_to_redeem_pct)}
              </p>
            </div>
            <div className="rounded-lg bg-muted p-3">
              <p className="text-xs text-muted-foreground">Overall</p>
              <p className="font-heading text-lg font-semibold tabular-nums">
                {pct(funnel.lead_to_redeem_pct)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5 lg:col-span-7 shadow-[var(--shadow-card)]" data-testid="analytics-distribution-card">
          <div className="flex items-center gap-2 mb-3">
            <PieIcon className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Prize distribution</h2>
          </div>
          {pieData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">
              No spins yet — distribution will appear here.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {pieData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={entry.color || `hsl(var(--chart-${(i % 5) + 1}))`}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--card))",
                        borderRadius: 12,
                        border: "1px solid hsl(var(--border))",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5">
                {distribution.map((d, i) => (
                  <div
                    key={d.label}
                    className="flex items-center justify-between gap-3 text-sm py-1 border-b last:border-0 border-border"
                    data-testid="analytics-distribution-row"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="h-3 w-3 rounded-sm shrink-0"
                        style={{ backgroundColor: d.color || `hsl(var(--chart-${(i % 5) + 1}))` }}
                      />
                      <span className="truncate">{d.label}</span>
                      {d.is_better_luck && (
                        <Badge variant="outline" className="text-[10px] py-0">L</Badge>
                      )}
                    </div>
                    <div className="text-muted-foreground tabular-nums">
                      {fmt(d.count)}
                      <span className="ml-2 text-xs">
                        ({pct((d.count / Math.max(funnel.leads, 1)) * 100)})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Top slices bar */}
      <Card className="p-5 shadow-[var(--shadow-card)]" data-testid="analytics-top-slices-card">
        <h2 className="font-heading text-lg font-semibold mb-3">Top winning prizes</h2>
        {topSlices.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">No data yet.</p>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={topSlices} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    borderRadius: 12,
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {topSlices.map((s, i) => (
                    <Cell key={i} fill={s.color || `hsl(var(--chart-${(i % 5) + 1}))`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Spins over time */}
      <Card className="p-5 shadow-[var(--shadow-card)]" data-testid="analytics-timeseries-card">
        <h2 className="font-heading text-lg font-semibold mb-3">Spins over time (30 days)</h2>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => v?.slice(5)}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  borderRadius: 12,
                  border: "1px solid hsl(var(--border))",
                }}
              />
              <Line
                type="monotone"
                dataKey="spins"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Hour heatmap + Day-of-week */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="p-5 lg:col-span-7 shadow-[var(--shadow-card)]" data-testid="analytics-hour-card">
          <h2 className="font-heading text-lg font-semibold mb-3">By hour of day</h2>
          <HourHeatmap data={hourSeries} />
          <p className="text-xs text-muted-foreground mt-2">Each cell is an hour (0–23). Darker = more spins.</p>
        </Card>
        <Card className="p-5 lg:col-span-5 shadow-[var(--shadow-card)]" data-testid="analytics-dow-card">
          <h2 className="font-heading text-lg font-semibold mb-3">By day of week</h2>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={dowSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    borderRadius: 12,
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Bar dataKey="spins" radius={[8, 8, 0, 0]} fill={primary} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-5 shadow-[var(--shadow-card)]" data-testid="analytics-messages-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Retargeting messages</h2>
          </div>
          <span className="text-sm text-muted-foreground tabular-nums">
            <span className="font-heading text-foreground">{fmt(data.messages_sent)}</span> sent
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Go to <Link to="/vendor/retargeting" className="text-primary hover:underline">Retargeting</Link> to send a new message.
        </p>
      </Card>
    </div>
  );
}
